import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { take } from 'rxjs/operators';
import { LogService } from './log.service';

declare var googletag: any;

/** Google Publisher Tag kütüphanesinin adresi. */
const GPT_LIBRARY_URL = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

/** Kütüphanenin ikinci kez eklenmesini engellemek için kullanılan script id'si. */
const GPT_SCRIPT_ID = 'gpt-library';

/** GTM'in gpt.js'i yüklemesi için beklenen süre (ms). */
const GPT_READY_TIMEOUT_MS = 10000;

/** Yedek yol (doğrudan gpt.js) denendikten sonra beklenen süre (ms). */
const GPT_FALLBACK_TIMEOUT_MS = 5000;

/** Graylog'da alarm kurulabilmesi için kullanılan sabit hata tipi. */
const GPT_LOG_ERROR_TYPE = 'GPT_AD_LOAD_FAILURE';

/** Slot tanımı: [ad unit path, boyut, DOM element id]. */
type AdSlotDefinition = [string, [number, number], string];

/**
 * Şablonlarda ve bileşenlerde ortak kullanılan slot element id'leri.
 *
 * `displayAndRefreshAds` çağrısı ile `id='...'` attribute'unun birbirinden
 * sessizce ayrışmasını engellemek için id'ler tek yerde tutuluyor.
 */
export const GPT_AD_SLOT_IDS = {
  /** Etkinlik / mekan / etkinlik-grup sayfalarındaki yan sütun 300x600. */
  SIDE_BANNER_300X600: 'div-gpt-ad-1664353181968-0',
  /** Özel etkinlik grubuna ait 330x600 (bkz. GPT_SPECIAL_EVENT_GROUP_ID). */
  SIDE_BANNER_330X600: 'div-gpt-ad-1735133701238-0',
} as const;

/**
 * 330x600 slot'unun gösterildiği özel etkinlik grubu kimliği.
 *
 * Daha önce yalnızca `event-group.component.html` şablonuna gömülüydü; bileşen
 * tarafında da gerektiği için buraya taşındı.
 */
export const GPT_SPECIAL_EVENT_GROUP_ID = '456241531';

/**
 * DFP slot tanımları.
 *
 * Bu liste daha önce index.html'deki inline script'te duruyordu; SSR çıktısına
 * gömülmemesi için buraya taşındı (bkz. sınıf dokümantasyonu).
 */
const AD_SLOTS: ReadonlyArray<AdSlotDefinition> = [
  ['/61630194/Homepage_1000x90', [1000, 90], 'div-gpt-ad-1594640818191-0'],
  ['/61630194/Event1000x90_1', [1000, 90], 'div-gpt-ad-1782990736608-0'],
  ['/61630194/Event1000x90_2', [1000, 90], 'div-gpt-ad-1782991325116-0'],
  ['/61630194/homepageResposive320', [320, 50], 'div-gpt-ad-1505377893829-0'],
  ['/61630194/320x50', [320, 50], 'div-gpt-ad-1782991448607-0'],
  ['/61630194/Even_320x50', [320, 50], 'div-gpt-ad-1782991599409-0'],
  ['/61630194/Homepage_300x250', [300, 250], 'div-gpt-ad-1480597952564-0'],
  ['/61630194/Homepage_305x110_1st', [305, 110], 'div-gpt-ad-1480597656496-0'],
  ['/61630194/Homepage_300x110_2nd', [305, 110], 'div-gpt-ad-1480665340802-0'],
  ['/61630194/Event1000x90', [1000, 90], 'div-gpt-ad-1664352640071-0'],
  ['/61630194/Mobile_Masthead_Event_Pages_320x50', [320, 50], 'div-gpt-ad-1664352779263-0'],
  ['/61630194/Event_Pages_Banner_300x600', [300, 600], 'div-gpt-ad-1664353181968-0'],
  ['/61630194/HomePage_370x145', [370, 145], 'div-gpt-ad-1784641014221-0'],
  ['/61630194/Ana_Sayfa_Slot_1_370x145', [370, 145], 'div-gpt-ad-1783604120201-0'],
  ['/61630194/Ana_Sayfa_Slot_2_370x145', [370, 145], 'div-gpt-ad-1783604312466-0'],
  ['/61630194/OzelDenemeEventGroup330x600', [330, 600], 'div-gpt-ad-1735133701238-0'],
];

/**
 * GPT (Google Publisher Tag) reklamlarını yönetir.
 *
 * Reklam altyapısının tamamı — kütüphanenin yüklenmesi, slot tanımları ve
 * display/refresh çağrıları — bilinçli olarak bu servise toplandı ve
 * `isPlatformBrowser` kontrolünün arkasına alındı. Böylece SSR sunucusu
 * hiçbir reklam kodu çalıştırmaz ve sunucudan dönen HTML'e reklam script'i
 * gömülmez; her şey kullanıcının tarayıcısında, hydration sonrası başlar.
 */
@Injectable({
  providedIn: 'root'
})
export class GptAdService {
  private isBrowser: boolean;
  private initialized = false;

  /** Yedek yükleme yolunun aynı sayfa oturumunda bir kez denenmesini sağlar. */
  private fallbackAttempted = false;

  /** Arıza logunun sayfa oturumu başına yalnızca bir kez gönderilmesini sağlar. */
  private loadFailureReported = false;

  constructor(@Inject(PLATFORM_ID) platformId: Object,
              private logService: LogService) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * GTM üzerinden gelen gpt.js kütüphanesinin (googletag.apiReady ve googletag.pubads)
   * yüklenmesini asenkron olarak bekler (maksimum 10 saniye).
   */
  private waitForGptReady(timeoutMs = GPT_READY_TIMEOUT_MS): Promise<boolean> {
    return new Promise((resolve) => {
      const check = (startTime: number) => {
        const isReady =
          typeof googletag !== 'undefined' &&
          (window as any).googletag?.apiReady &&
          typeof (window as any).googletag?.pubads === 'function';

        if (isReady) {
          resolve(true);
        } else if (Date.now() - startTime < timeoutMs) {
          setTimeout(() => check(startTime), 150);
        } else {
          // Uyarı burada basılmıyor: zaman aşımı tek başına arıza değil,
          // yedek yol denenecek. Nihai arıza `reportLoadFailure()` içinde loglanır.
          resolve(false);
        }
      };
      check(Date.now());
    });
  }

  displayAndRefreshAds(slotIds: string[]): void {
    if (!this.isBrowser) return;

    this.ensureGptReady().then((isReady) => {
      if (!isReady) return;

      const gpt = this.initialize();

      gpt.cmd.push(() => {
        try {
          const pubads = googletag.pubads();
          const slots = pubads ? pubads.getSlots() : [];
          const slotsToRefresh: any[] = [];

          slotIds.forEach(id => {
            const el = document.getElementById(id);
            // Check element existence and visibility
            if (el && (el.offsetWidth > 0 || el.offsetHeight > 0 || el.offsetParent !== null)) {
              googletag.display(id);
              if (slots && slots.length > 0) {
                const matchedSlot = slots.find((s: any) => s.getSlotElementId() === id);
                if (matchedSlot) {
                  slotsToRefresh.push(matchedSlot);
                }
              }
            }
          });

          if (slotsToRefresh.length > 0) {
            googletag.pubads().refresh(slotsToRefresh);
          }
        } catch (err) {
          console.warn('GptAdService execution warning:', err);
        }
      });
    });
  }

  /**
   * gpt.js'in hazır olmasını garanti etmeye çalışır.
   *
   * Birincil yol GTM'dir. GTM etiketi durdurulmuş, hatalı yapılandırılmış veya
   * engellenmişse tüm reklam envanteri sessizce kaybolduğu için burada iki
   * emniyet katmanı var:
   *   1. Zaman aşımında kütüphane doğrudan `loadLibrary()` ile yüklenir,
   *   2. O da başarısız olursa arıza Graylog'a raporlanır (alarm kurulabilsin).
   */
  private async ensureGptReady(): Promise<boolean> {
    if (await this.waitForGptReady()) {
      return true;
    }

    // GTM yolu çalışmadı: kütüphaneyi kendimiz yükleyip ikinci bir şans veriyoruz.
    if (!this.fallbackAttempted) {
      this.fallbackAttempted = true;
      this.loadLibrary();
    }

    if (await this.waitForGptReady(GPT_FALLBACK_TIMEOUT_MS)) {
      return true;
    }

    this.reportLoadFailure();
    return false;
  }

  /**
   * gpt.js hiç yüklenemediğinde bir kez backend'e log basar.
   *
   * Reklam gelirinin tamamı bu kütüphaneye bağlı olduğu ve arıza tarayıcıda
   * sessiz kaldığı için, `console.warn` yeterli değil: prod'da kimse görmüyor.
   * `printClientErrorLog` kaydı Graylog'un `NewSite Crmapi` akışına düşer ve
   * `GPT_AD_LOAD_FAILURE` tipiyle alarm kurulabilir.
   */
  private reportLoadFailure(): void {
    if (this.loadFailureReported) return;
    this.loadFailureReported = true;

    const message =
      `gpt.js yüklenemedi: GTM yolu ${GPT_READY_TIMEOUT_MS} ms, ` +
      `yedek yol ${GPT_FALLBACK_TIMEOUT_MS} ms içinde hazır olmadı. ` +
      `Tüm GPT reklam slotları gösterilmiyor. url=${(window as any).location?.href ?? ''}`;

    console.warn(`GptAdService: ${message}`);

    try {
      this.logService.printClientErrorLog(GPT_LOG_ERROR_TYPE, message, '')
        .pipe(take(1))
        .subscribe({ error: () => { /* log gönderimi başarısızsa sessizce geç */ } });
    } catch {
      // Log servisi kullanılamıyorsa reklam akışını etkilemeden devam et.
    }
  }

  /**
   * Komut kuyruğunu hazırlar, slot'ları tanımlar.
   * Tarayıcı oturumu boyunca yalnızca bir kez etki eder; sonraki çağrılar
   * yalnızca mevcut `googletag` nesnesini döndürür.
   *
   * @returns Komut kuyruğuna sahip `googletag` nesnesi
   */
  private initialize(): any {
    const gpt = (window as any).googletag = (window as any).googletag || { cmd: [] };

    if (this.initialized) {
      return gpt;
    }
    this.initialized = true;

    gpt.cmd.push(() => {
      try {
        googletag.pubads().addEventListener('slotRenderEnded', (event: any) => {
          // OptanonConsent çerezindeki intType=2 kontrolü
          if (!this.hasOptanonConsentIntTypeTwo()) {
            return;
          }

          const slotId = event.slot?.getSlotElementId();
          if (slotId) {
            const el = document.getElementById(slotId);
            if (el) {
              if (event.isEmpty) {
                el.style.display = 'none';
              } else {
                el.style.display = 'inline-block';
              }
            }
          }
        });

        AD_SLOTS.forEach(([adUnitPath, size, elementId]) => {
          // defineSlot, aynı element id'si için ikinci çağrıda null döner.
          const slot = googletag.defineSlot(adUnitPath, size, elementId);
          if (slot) {
            slot.addService(googletag.pubads());
          }
        });

        googletag.pubads().collapseEmptyDivs();
        googletag.enableServices();
      } catch (err) {
        console.warn('GptAdService slot definition warning:', err);
      }
    });

    return gpt;
  }

  /**
   * gpt.js'i <head>'e ekler. Kütüphane hazırsa ya da başka bir kaynak
   * (ör. Google Tag Manager) script'i zaten eklemişse tekrar yüklemez —
   * gpt.js'in iki kez yüklenmesi slot tanımlarını bozar.
   */
  private loadLibrary(): void {
    const isAlreadyLoaded =
      (window as any).googletag?.apiReady ||
      document.getElementById(GPT_SCRIPT_ID) ||
      document.querySelector('script[src*="tag/js/gpt.js"]');

    if (isAlreadyLoaded) {
      return;
    }

    const script = document.createElement('script');
    script.id = GPT_SCRIPT_ID;
    script.async = true;
    script.src = GPT_LIBRARY_URL;
    script.onerror = () => console.warn(`GptAdService could not load ${GPT_LIBRARY_URL}`);
    document.head.appendChild(script);
  }

  /**
   * Çerezdeki (Cookie) OptanonConsent değerini okur ve `intType=2` (kullanıcının rıza onay etkileşimi)
   * olup olmadığını kontrol eder.
   */
  private hasOptanonConsentIntTypeTwo(): boolean {
    if (!this.isBrowser) return false;

    const cookieValue = this.getCookie('OptanonConsent');
    if (!cookieValue) return false;

    const decoded = decodeURIComponent(cookieValue);
    if (decoded.includes('intType=2') || cookieValue.includes('intType=2')) {
      return true;
    }

    try {
      const params = new URLSearchParams(decoded);
      return params.get('intType') === '2';
    } catch {
      return false;
    }
  }

  private getCookie(name: string): string | null {
    if (!this.isBrowser) return null;
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }
}
